<template>
  <b-modal
    id="dashboard-detail-modal"
    :title="modalTitle"
    size="xl"
    scrollable
    centered
    no-close-on-backdrop
    :visible="visible"
    @change="handleVisibilityChange"
    @hidden="resetModal"
  >
    <template #modal-header="{ close }">
      <h5 class="modal-title">{{ modalTitle }}</h5>
      <b-button variant="flat-secondary" @click="close()">
        <feather-icon icon="XIcon" />
      </b-button>
    </template>

    <b-card no-body>
      <b-card-header>
        <b-card-title>Summary</b-card-title>
      </b-card-header>
      <b-card-body>
        <b-row v-if="loading" class="text-center">
          <b-col><b-spinner label="Loading..."></b-spinner></b-col>
        </b-row>
        <b-row v-else-if="error" class="text-center text-danger">
          <b-col>Error loading summary: {{ error }}</b-col>
        </b-row>
        <b-row v-else-if="!summary || Object.keys(summary).length === 0">
          <b-col>No summary data available.</b-col>
        </b-row>
        <b-row v-else>
          <b-col v-for="(value, key) in summary" :key="key" cols="auto" class="mb-1">
            <b-badge pill variant="light-primary" class="p-1">
              <strong>{{ formatKey(key) }}:</strong> {{ formatValue(key, value) }}
            </b-badge>
          </b-col>
        </b-row>
      </b-card-body>
    </b-card>

    <b-card no-body class="mt-2">
      <b-card-header>
        <b-card-title>Details</b-card-title>
        <b-button v-if="viewMode === 'detail' && selectedUserName" variant="outline-secondary" size="sm" class="mr-2" @click="backToUserList">
          ← Back to user list
        </b-button>
        <b-input-group size="sm" class="w-25">
          <b-form-input v-model="searchTerm" placeholder="Search table..." debounce="500" />
          <b-input-group-append>
            <b-button @click="searchTerm = ''" :disabled="!searchTerm">Clear</b-button>
          </b-input-group-append>
        </b-input-group>
      </b-card-header>
      <b-card-body>
        <b-row v-if="loading" class="text-center">
          <b-col><b-spinner label="Loading..."></b-spinner></b-col>
        </b-row>
        <b-row v-else-if="error" class="text-center text-danger">
          <b-col>Error loading details: {{ error }}</b-col>
        </b-row>
        <b-row v-else-if="!paginatedItems || paginatedItems.length === 0">
          <b-col>No detail data available.</b-col>
        </b-row>
        <b-row v-else>
          <b-col>
            <b-table
              striped
              hover
              responsive
              :items="paginatedItems"
              :fields="tableFields"
              :current-page="tableCurrentPage"
              :per-page="perPage"
              :sort-by.sync="sortBy"
              :sort-desc.sync="sortDesc"
              :tbody-tr-class="detailRowTrClass"
              small
              @row-clicked="onRowClicked"
            >
            <template #cell(remarks)="data">
                {{ data.value.remarks }}
            </template>
              <template #cell()="data">
                {{ formatValue(data.field.key, data.value) }}
              </template>
            </b-table>
            <b-pagination
              v-model="currentPage"
              :total-rows="totalRows"
              :per-page="perPage"
              align="center"
              class="my-0"
            ></b-pagination>
          </b-col>
        </b-row>
      </b-card-body>
    </b-card>

    <template #modal-footer="{ close }">
      <b-button variant="primary" @click="close()">
        Ok
      </b-button>
    </template>
  </b-modal>
</template>

<script>
import {
  BModal, BButton, BCard, BCardHeader, BCardTitle, BCardBody, BRow, BCol, BSpinner,
  BBadge, BTable, BPagination, BFormInput, BInputGroup, BInputGroupAppend
} from 'bootstrap-vue';
import { makeDashboardApiRequest } from '@/components/dashboard/dashboardApi';

export default {
  components: {
    BModal, BButton, BCard, BCardHeader, BCardTitle, BCardBody, BRow, BCol, BSpinner,
    BBadge, BTable, BPagination, BFormInput, BInputGroup, BInputGroupAppend
  },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    widgetTitle: {
      type: String,
      required: true,
    },
    detailEndpoint: {
      type: String,
      required: true,
    },
    detailParams: {
      type: Object,
      default: () => ({}),
    },
    filters: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      loading: false,
      error: null,
      summary: {},
      detailData: [],
      currentPage: 1,
      perPage: 10,
      sortBy: null,
      sortDesc: false,
      searchTerm: '',
      totalServerRows: 0,
      viewMode: 'summary',
      selectedUserId: null,
      selectedUserName: '',
    };
  },
  computed: {
    isSummaryByUser() {
      return !!(this.summary && this.summary.summary_by_user);
    },
    modalTitle() {
      return `${this.widgetTitle} Details`;
    },
    tableFields() {
      if (this.detailData.length === 0) return [];
      const hidden = new Set(['lead_id', 'cfirstid']);
      const keys = Object.keys(this.detailData[0]).filter((k) => !hidden.has(k));
      return keys.map(key => ({
        key: key,
        label: this.formatKey(key),
        sortable: true,
      }));
    },
    filteredItems() {
      const term = this.searchTerm.toLowerCase();
      if (!term) return this.detailData;
      return this.detailData.filter(item =>
        Object.values(item).some(value =>
          String(value).toLowerCase().includes(term)
        )
      );
    },
    totalRows() {
      return this.totalServerRows != null ? this.totalServerRows : this.filteredItems.length;
    },
    tableCurrentPage() {
      return this.totalServerRows != null ? 1 : this.currentPage;
    },
    paginatedItems() {
      if (this.totalServerRows != null) {
        return this.filteredItems;
      }
      const items = this.filteredItems;
      const start = (this.currentPage - 1) * this.perPage;
      const end = start + this.perPage;
      return items.slice(start, end);
    },
  },
  watch: {
    detailEndpoint: {
      immediate: true,
      handler(newEndpoint) {
        if (newEndpoint) {
          this.currentPage = 1;
          this.fetchDetailData();
        }
      },
    },
    filters: {
      deep: true,
      handler() {
        if (this.detailEndpoint) {
          this.currentPage = 1;
          this.fetchDetailData();
        }
      },
    },
    currentPage(newPage, oldPage) {
      if (oldPage == null || newPage === oldPage || !this.visible || !this.detailEndpoint) return;
      if (this.viewMode === 'summary' && this.isSummaryByUser) return;
      if (this.viewMode === 'detail' && this.detailParams.drillDetailEndpoint) {
        this.fetchDrillDetail(this.selectedUserId);
      } else {
        this.fetchDetailData(newPage);
      }
    },
    searchTerm() {
      this.currentPage = 1; // Reset to first page on search
    },
  },
  methods: {
    handleVisibilityChange(visible) {
      this.$emit('update:visible', visible);
    },
    async fetchDetailData(requestedPage) {
      const page = requestedPage !== undefined ? requestedPage : this.currentPage;
      this.loading = true;
      this.error = null;
      try {
        const params = {
          ...this.filters,
          ...this.detailParams,
          limit: this.perPage,
          offset: (page - 1) * this.perPage,
        };
        delete params.drillDetailEndpoint;
        const response = await makeDashboardApiRequest(this.detailEndpoint, params);
        if (response.status && response.data) {
          this.summary = response.data.summary || {};
          this.detailData = response.data.rows || [];
          if (response.data.summary && response.data.summary.summary_by_user) {
            this.totalServerRows = null;
            this.viewMode = 'summary';
            this.selectedUserId = null;
            this.selectedUserName = '';
          } else {
            this.totalServerRows = response.data.summary.total_count ?? 0;
          }
        } else {
          this.error = response.message || 'Unknown error fetching data';
        }
      } catch (error) {
        this.error = error.message || 'Failed to fetch data';
        console.error('Error fetching dashboard detail:', error);
      } finally {
        this.loading = false;
      }
    },
    async fetchDrillDetail(userId) {
      const endpoint = this.detailParams.drillDetailEndpoint;
      if (!endpoint) return;
      this.loading = true;
      this.error = null;
      try {
        const params = {
          ...this.filters,
          limit: this.perPage,
          offset: (this.currentPage - 1) * this.perPage,
          employee_id: userId,
        };
        const response = await makeDashboardApiRequest(endpoint, params);
        if (response.status && response.data) {
          this.summary = response.data.summary || {};
          this.detailData = response.data.rows || [];
          this.totalServerRows = response.data.summary.total_count ?? 0;
        } else {
          this.error = response.message || 'Unknown error fetching data';
        }
      } catch (error) {
        this.error = error.message || 'Failed to fetch data';
        console.error('Error fetching drill detail:', error);
      } finally {
        this.loading = false;
      }
    },
    detailRowTrClass(item, type) {
      if (type !== 'row' || !item) return null;
      if (this.isSummaryByUser && this.detailParams.drillDetailEndpoint) return 'clickable-row';
      if (this.resolveLeadId(item)) return 'clickable-row';
      return null;
    },
    resolveLeadId(item) {
      if (!item || typeof item !== 'object') return null;
      const raw = item.lead_id ?? item.cfirstid;
      if (raw !== undefined && raw !== null && raw !== '') {
        const n = Number(raw);
        if (Number.isFinite(n) && n > 0) return n;
      }
      if (item.type === 'Lead' && item.id != null && item.id !== '') {
        const n = Number(item.id);
        if (Number.isFinite(n) && n > 0) return n;
      }
      return null;
    },
    openFollowupForLead(leadId) {
      this.$router.push(`/followup/followupinfo?id=${leadId}`).catch(() => {});
      this.$emit('update:visible', false);
    },
    onRowClicked(item) {
      if (this.isSummaryByUser && this.detailParams.drillDetailEndpoint) {
        this.selectedUserId = item.user_id != null ? item.user_id : (item.user_name === 'Unassigned' ? null : item.user_id);
        this.selectedUserName = item.user_name || 'User';
        this.viewMode = 'detail';
        this.currentPage = 1;
        this.fetchDrillDetail(this.selectedUserId);
        return;
      }
      const leadId = this.resolveLeadId(item);
      if (leadId) {
        this.openFollowupForLead(leadId);
      }
    },
    backToUserList() {
      this.viewMode = 'summary';
      this.selectedUserId = null;
      this.selectedUserName = '';
      this.currentPage = 1;
      this.fetchDetailData();
    },
    formatKey(key) {
      return key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase()).replace(/_/g, ' ');
    },
    formatValue(key, value) {
      if (typeof value === 'number' && (key.includes('conversion_rate') || key.includes('rate') || key.includes('percentage'))) {
        return `${Number(value).toFixed(2)}%`;
      }
      if (typeof value === 'number' && (key.includes('value') || key.includes('revenue') || key.includes('payment'))) {
        return `₹${value.toLocaleString('en-IN')}`; // Indian Rupee format
      }
      if (typeof value === 'boolean') {
        return value ? 'Yes' : 'No';
      }
      if (typeof value === 'string' && value.match(/^\d{4}-\d{2}-\d{2}( \d{2}:\d{2}:\d{2})?$/)) {
          return new Date(value).toLocaleDateString('en-IN', {
              year: 'numeric', month: 'short', day: 'numeric',
              hour: value.includes(':') ? '2-digit' : undefined,
              minute: value.includes(':') ? '2-digit' : undefined,
              second: value.includes(':') ? '2-digit' : undefined,
              hour12: false,
          });
      }
      return value;
    },
    resetModal() {
      this.summary = {};
      this.detailData = [];
      this.loading = false;
      this.error = null;
      this.currentPage = 1;
      this.searchTerm = '';
      this.sortBy = null;
      this.sortDesc = false;
      this.viewMode = 'summary';
      this.selectedUserId = null;
      this.selectedUserName = '';
      this.$emit('modal-closed');
    },
  },
};
</script>

<style lang="scss" scoped>
.modal-title {
  font-size: 1.5rem;
}
.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.b-table {
  ::v-deep th {
    white-space: nowrap;
  }
}
.clickable-row {
  cursor: pointer;
}
.clickable-row:hover {
  background-color: rgba(0, 0, 0, 0.05);
}
</style>
